/**
 *
 * @module accountClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/account.md
 * @see https://github.bamtech.co/services-commons/public-api/blob/master/swagger/services/account.md
 *
 */

/* eslint-disable camelcase */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import Token from './../token/entry';
import HttpClient from './../platforms/httpClient';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import Account from './account';
import AccountClientConfiguration from './accountClientConfiguration';
import AccountClientEndpoint from './accountClientEndpoint';
import AccountGrant from './accountGrant';
import CreateAccountResult from './createAccountResult';
import DeleteUserProfileResult from './deleteUserProfileResult';
import UserProfile from './userProfile';
import UpdateUserProfileResult from './updateUserProfileResult';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;
const AccountClientDustUrnRefernce = DustUrnReference.services.account.accountClient;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;

const {
    instanceStrict,
    object,
    nonEmptyObject,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class AccountClient
 * @desc Provides a data client that can be used to access account services.
 *
 */
export default class AccountClient {

    /**
     *
     * @param {AccountClientConfiguration} accountClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(accountClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'AccountClient(accountClientConfiguration, logger, httpClient)',
            get accountClientConfiguration() {
                return `${this.ctor} needs AccountClientConfiguration instance`;
            }
        };

        instanceStrict(accountClientConfiguration, AccountClientConfiguration, errorMessage.accountClientConfiguration, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {AccountClientConfiguration}
         *
         */
        this.config = accountClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc determines if you should refresh the access token
         *
         */
        this.refreshAccessTokenHeader = 'X-BAMTech-Refresh-Access-Token';

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(AccountClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @param {String} options.identityToken - Identity to create the account from.
     * @param {Object} options.attributes - Attributes to create with the account.
     * @param {SDK.Services.Token.AccessToken} options.accessToken - The access token to provide user context
     * @note {Object} [options.metadata] - Object which may hold additional information about the account
     * @desc Create an account by providing an id token and some registration information.
     * @returns {Promise<CreateAccountResult>} The account ID if successfully created.
     *
     */
    createAccount(options) {

        const errorMessage = {
            method: 'AccountClient.createAccount(options)',
            get options() {
                return `${this.method} should be a non-empty Object`;
            },
            get identityToken() {
                return `${this.method} identityToken should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            },
            get metadata() {
                return `${this.method} metadata should be an Object`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { identityToken, attributes, accessToken, metadata } = options;

        nonEmptyString(identityToken, errorMessage.identityToken, InvalidArgumentException);
        object(attributes, errorMessage.attributes, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        if (Check.assigned(metadata)) {
            object(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { dustEnabled, logger, httpClient } = this;

        const body = Object.assign({ id_token: identityToken }, attributes, metadata);

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.createAccount,
            body
        );

        logger.info(AccountClient.name, 'Attempting to create account.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.createAccount,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { accountId } = data;

            return Promise.resolve(new CreateAccountResult(accountId));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Get an account grant by providing some proof of identity e.g. id token.
     * @returns {Promise<Account>} The account resource identified in the access token.
     *
     */
    getCurrentAccount(accessToken) {

        const errorMessage = {
            method: 'AccountClient.getCurrentAccount(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.getCurrentAccount
        );

        logger.info(AccountClient.name, 'Get current account based on provided token.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.getCurrentAccount,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { accountId, activeProfile, attributes, identities } = data;

            const activeUserProfile = new UserProfile(activeProfile);
            const options = {
                accountId,
                activeUserProfile,
                attributes,
                identities
            };

            return Promise.resolve(new Account(options));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} identityToken - Identity to get the account grant for.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Get an account grant by providing some proof of identity e.g. id token.
     * @returns {Promise<AccountGrant>} An account grant that can be exchanged for an access token if successful.
     *
     */
    createAccountGrant(identityToken, accessToken) {

        const errorMessage = {
            method: 'AccountClient.createAccountGrant(identityToken, accessToken)',
            get identityToken() {
                return `${this.method} identityToken should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(identityToken, errorMessage.identityToken, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.createAccountGrant,
            {
                id_token: identityToken
            }
        );

        logger.log(AccountClient.name, 'Attempting to create account grant.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.createAccountGrant,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { assertion, grant_type } = data;

            return Promise.resolve(new AccountGrant({
                assertion,
                grant_type
            }));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {Object} attributes - The account attributes to update.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Update the account attributes of the account identified in the access token,
     * if field(s) are missing from the request body, the original value will be left intact.
     * @returns {Promise<Void>} An awaitable task indicating settings were updated successfully.
     *
     */
    updateAttributes(attributes, accessToken) {

        const errorMessage = {
            method: 'AccountClient.updateAttributes(attributes, accessToken)',
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        object(attributes, errorMessage.attributes, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.updateAccountAttributes,
            attributes
        );

        logger.info(AccountClient.name, 'Update account attributes.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.updateAttributes,
            payload,
            method: HttpMethod.PATCH
        });

        if (Object.keys(attributes).length) {
            logger.info(AccountClient.name, `Account attributes to update - "${JSON.stringify(attributes)}".`);
        }

        return httpClient.patch(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {String} profileName the name of the profile
     * @param {Object} [attributes={}] attributes associated with this profile
     * @param {Object} [metadata={}] optional metadata used when creating an account
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<UserProfile>}
     *
     */
    createUserProfile(profileName, attributes = {}, metadata = {}, accessToken) {

        const errorMessage = {
            method: 'AccountClient.createUserProfile(profileName, attributes, metadata, accessToken)',
            get profileName() {
                return `${this.method} profileName should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(profileName, errorMessage.profileName, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.createUserProfile,
            {
                profileName,
                attributes,
                metadata
            }
        );

        logger.info(AccountClient.name, 'Create user profile.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.createUserProfile,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(new UserProfile(data));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {String} profileId
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<SDK.Services.Token.DeleteUserProfileResult>}
     *
     */
    deleteUserProfile(profileId, accessToken) {

        const errorMessage = {
            method: 'AccountClient.deleteUserProfile(profileId, accessToken)',
            get profileId() {
                return `${this.method} profileId should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(profileId, errorMessage.profileId, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.deleteUserProfile,
            null,
            profileId
        );

        logger.info(AccountClient.name, 'Delete user profile.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.deleteUserProfile,
            payload,
            method: HttpMethod.DELETE
        });

        return httpClient.del(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            let accountGrant;

            const { data } = response;

            if (Check.assigned(data.assertion)) {
                accountGrant = new AccountGrant(data);
            }

            return Promise.resolve(new DeleteUserProfileResult(accountGrant));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {String} profileId
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<UserProfile>}
     *
     */
    getUserProfile(profileId, accessToken) {

        const errorMessage = {
            method: 'AccountClient.getUserProfile(profileId, accessToken)',
            get profileId() {
                return `${this.method} profileId should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(profileId, errorMessage.profileId, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.getUserProfile,
            null,
            profileId
        );

        logger.info(AccountClient.name, 'Get user profile.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.getUserProfile,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(new UserProfile(data));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<Array<UserProfile>>}
     *
     */
    getUserProfiles(accessToken) {

        const errorMessage = {
            method: 'AccountClient.getUserProfiles(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.getUserProfiles,
            null
        );

        logger.info(AccountClient.name, 'Get user profiles.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.getUserProfiles,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data = [] } = response;

            const userProfiles = data.map((profile) => new UserProfile(profile));

            return Promise.resolve(userProfiles);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {String} profileId
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<SDK.Services.Token.AccountGrant>}
     *
     */
    setActiveUserProfile(profileId, accessToken) {

        const errorMessage = {
            method: 'AccountClient.setActiveUserProfile(profileId, accessToken)',
            get profileId() {
                return `${this.method} profileId should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(profileId, errorMessage.profileId, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.setActiveUserProfile,
            null,
            profileId
        );

        logger.info(AccountClient.name, 'Set active user profile.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.setActiveUserProfile,
            payload,
            method: HttpMethod.PUT
        });

        return httpClient.put(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(new AccountGrant(data));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<UserProfile>}
     *
     */
    getActiveUserProfile(accessToken) {

        const errorMessage = {
            method: 'AccountClient.getActiveUserProfile(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.getActiveUserProfile,
            null
        );

        logger.info(AccountClient.name, 'Get active user profile.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.getActiveUserProfile,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(new UserProfile(data));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {UserProfile} userProfile
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @returns {Promise<UpdateUserProfileResult>}
     *
     */
    updateUserProfile(userProfile, accessToken) {

        const errorMessage = {
            method: 'AccountClient.updateUserProfile(userProfile, accessToken)',
            get userProfile() {
                return `${this.method} needs UserProfile instance`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(userProfile, UserProfile, errorMessage.userProfile, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;
        const { profileId, profileName, attributes, metadata } = userProfile;

        const payload = this.getPayload(
            accessToken,
            AccountClientEndpoint.updateUserProfile,
            {
                profileId,
                profileName,
                attributes,
                metadata
            },
            profileId
        );

        logger.info(AccountClient.name, 'Set active user profile.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountClient.name,
            urn: AccountClientDustUrnRefernce.updateUserProfile,
            payload,
            method: HttpMethod.PATCH
        });

        return httpClient.patch(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { refreshAccessTokenHeader } = this;
            const { headers } = response;

            const refreshAccessToken = headers.get(refreshAccessTokenHeader) === 'true';

            return Promise.resolve(new UpdateUserProfileResult(refreshAccessToken));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {String} rel
     * @param {Object} body
     * @param {String} profileId
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body, profileId) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { headers } = endpoint;
        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        let href = endpoint.href;

        if (endpoint.templated && profileId) {
            href = href.replace(/\{profileId\}/gi, profileId);
        }

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/account/accountClient.js